import mixpanel from 'mixpanel-browser';
import { Location } from 'history';
import { Event, Events, Properties } from '../enums/Analytics';
import {
  MIXPANEL_IS_ENABLED,
  MIXPANEL_PROJECT_TOKEN,
  REACT_APP_MODE,
} from '../config/env';
import { OrganizationSchema, UserSchema } from '../definitions/schemas';
import { toSnakeCase } from '../lib/String';
import { UserAuth } from '../definitions/userAuth';
import Datadog from './Datadog';
import OrganizationsStore from '../stores/observables/OrganizationsStore';
import RolesEnum from '../enums/Roles';
import UserStore, { UserRolesStore } from '../stores/observables/UserStore';
import Zipy from './Zipy';

// Extend Window interface to include disableMixpanelTracking property
declare global {
  interface Window {
    disableMixpanelTracking?: boolean;
  }
}

export type EventProperty = {
  [key: string]: string | number | boolean | undefined;
};

const DELAY_PAGEVIEW = 5000;

/**
 * Obtains the organization info and the role for the current user
 * under the current context, so it can be used as the default event properties
 * for all tracked events. Since this information varies according to the
 * use case, it's calculated for each tracked event instead of being treated
 * as User Properties.
 */
const getBaseProperties = (
  forcedOrganization?: OrganizationSchema
): Record<string, unknown> => {
  // The user context can be set at multiple stores
  const currentUser = UserStore.get()!;
  const currentRole = UserRolesStore.getCurrent();
  const currentOrg = OrganizationsStore.getCurrentOrganization();
  const currentRoleByOrg = currentOrg
    ? UserRolesStore.getRoleByOrganizationId(currentOrg.id)
    : undefined;

  const zipySessionUrl = window.zipy ? window.zipy.getCurrentSessionURL() : '';

  // Compose the base properties based on any context we found
  const baseProperties: EventProperty = {
    [Properties.STREAM]: 'DSR',
    [Properties.USER_ID]: currentUser?.id,
    [Properties.ZIPY_SESSION_URL]: zipySessionUrl,
  };

  if (currentUser?.member?.vendorUserId) {
    baseProperties[Properties.VENDOR_USER_ID] = currentUser.member.vendorUserId;
  }

  const org = forcedOrganization || currentRole?.organization || currentOrg;
  if (org) {
    baseProperties[Properties.MP_COMPANY] = org.name;
    baseProperties[Properties.ORGANIZATION_ID] = org.id;
    baseProperties[Properties.ORGANIZATION_NAME] = org.name;
    baseProperties[Properties.ORGANIZATION_TYPE] = org.tenantType || '';
    baseProperties[Properties.VENDOR] = toSnakeCase(org.vendor?.name || '');
  }

  const role = currentRole?.role || currentRoleByOrg?.role;
  if (forcedOrganization) {
    // we force 'admin' as the role if a forced organization param is given,
    // as this is only for the scenario where a new org has been created
    baseProperties[Properties.ROLE] = RolesEnum.ADMIN;
  } else if (role) {
    baseProperties[Properties.ROLE] = role.name;
  }

  return baseProperties;
};

/**
 * Builds an id that will be used as a User identifier in some of the analytics
 * tools such as Mixpanel. This is to distinguish where the users
 * come from, and avoid mixing up data because of mixed IDs values.
 *
 * For example:
 *  - User Id 3000 in Enable Us could be John Doe
 *  - User Id 3000 in Mindtickle could be Ashok Kumar
 *
 * @note If there's a scenario where the vendor's id has to be used instead,
 * then it's given priority and the prefixed id is used as a fallback. For
 * example: Mixpanel is handled in both
 * apps, so there could be IDs conflicts
 */
const buildTrackingUserId = (user: UserSchema) => {
  if (user.member?.vendorUserId) {
    return user.member.vendorUserId;
  }

  const userId = user.id;
  const org = OrganizationsStore.getCurrentOrganization();
  const prefix = org?.vendor?.analyticsPrefix;

  return prefix ? `${prefix}-${userId}` : userId;
};

class Analytics {
  private isGA: boolean;

  private isGA4: boolean;

  private isMixpanel: boolean;

  private isProduction: boolean;

  // True if any service is initialized
  public isTracking: boolean;

  constructor() {
    this.isGA = false;
    this.isGA4 = false;
    this.isMixpanel = false;
    this.isProduction = REACT_APP_MODE === 'PRODUCTION_MODE';
    this.isTracking = false;
  }

  public identify(
    user: UserSchema,
    organization?: OrganizationSchema,
    userAuth?: UserAuth
  ) {
    const userId = buildTrackingUserId(user);

    /**
     * Mixpanel identity
     * @see https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelidentify
     */
    if (this.isMixpanel) {
      console.info(`Mixpanel identify user: ${userId}`);
      mixpanel.identify(userId);
      mixpanel.people.set({
        [Properties.MP_EMAIL]: user.email,
        [Properties.MP_FIRST_NAME]: user.firstName,
        [Properties.MP_LAST_NAME]: user.lastName,
        [Properties.MP_NAME]: user.fullName,
        ...getBaseProperties(organization),
      });
    }

    Zipy.identify(userId, user, organization);
    // defaulting to the current org in the store if no forced organization was given
    const currentOrg = OrganizationsStore.getCurrentOrganization();
    const datadogOrganization = organization || currentOrg;
    Datadog.identify(userId, datadogOrganization, userAuth);
  }

  public anonymize() {
    /**
     * Mixpanel anonymize
     * @see https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelreset
     */
    if (this.isMixpanel) {
      console.info(`Mixpanel anonymized`);
      mixpanel.reset();
    }
  }

  /**
   * Execute page view tracking
   * Utility method to handle async scenarios
   */
  private _trackPageViewNow(location: Location) {
    const { pathname, search } = location;
    const pageviewPath = `${pathname}${search}`;
    console.info(`Tracking page view: ${pageviewPath}`);
    // Mixpanel: page viewed
    if (this.isMixpanel) {
      const matcher = /\/organization\/(\d*)/gi;
      /**
       * Track only internal views
       * @warn do not rely on the authenticated state because
       *       the first page load is always in guest mode
       */
      if (matcher.test(pathname)) {
        this.track(Events.PAGE_VIEWED, {
          // Provide the page uri (ie. /library, /reference-requested)
          [Properties.URL]: pathname.replace(matcher, ''),
        });
      }
    }
  }

  /**
   * Tracks the page view
   * Page views are supposed to be a "catch-all" metric
   *
   * @warn
   * 1. This event is fired whenever the history changes and it
   *    should only be triggered once to avoid false pageview hits.
   * 2. We tried our best to track the base properties (org data)
   */
  public trackPageView(location: Location) {
    const currentRole = UserRolesStore.getCurrent();
    /**
     * If there is no current role set yet, we can asumme:
     * - First load: user data is not set yet
     * - Public links: guest context
     * - Logout action
     *
     * We delay tracking the page views to wait for user data.
     * There is no better way than a timeout, because we can't
     * easily handle all guest and protected route scenarios.
     *
     * We already tried:
     * - Listening to UserRolesStore changes (can't guaranteed a value will be set)
     */
    if (!currentRole) {
      setTimeout(() => {
        this._trackPageViewNow(location);
      }, DELAY_PAGEVIEW);
      return;
    }
    // If there is already a role, track inmmediately
    this._trackPageViewNow(location);
  }

  /**
   * Track an event with Mixpanel if it's enabled.
   * Every event will have the organizationId, organizationName and role
   * properties, which are obtained from the current role context. This
   * information references the target organization to which the action
   * is being performed. For example: for a customer, the event will
   * track the target organization instead of the customer's own organization.
   *
   * @see https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpaneltrack
   */
  public track(event: Event, eventProperties?: EventProperty) {
    const baseProperties = getBaseProperties();
    const properties = {
      ...baseProperties,
      ...eventProperties,
    };

    // Logging to console for debug purposes if mixpanel tracking is off,
    // because otherwise, mixpanel on debug mode would log the info
    if (!this.isMixpanel) {
      console.log(
        `Tracking Mixpanel event: ${event}.\n`,
        `With payload: ${JSON.stringify(properties)}`
      );
      return;
    }

    mixpanel.track(event, properties);
  }

  /**
   * Initialize Mixpanel
   * @see https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelinit
   * @see https://docs.mixpanel.com/docs/tracking/reference/javascript-full-api-reference#mixpanelset_config
   */
  public registerMixpanel() {
    if (
      !MIXPANEL_PROJECT_TOKEN ||
      !MIXPANEL_IS_ENABLED ||
      (window && window.disableMixpanelTracking)
    ) {
      return;
    }

    console.info(`Mixpanel initialized`);

    mixpanel.init(MIXPANEL_PROJECT_TOKEN, {
      // @see https://docs.mixpanel.com/docs/tracking/how-tos/debugging#enable-debug-mode
      debug: !this.isProduction,
      // @see https://docs.mixpanel.com/docs/tracking/javascript-quickstart?projectToken=7e6e6808a3c76ca5eca132dcc4da7380#what-are-the-recommended-configuration-options
      persistence: 'localStorage',
    });

    this.isMixpanel = true;
    this.isTracking = true;
  }

  // Register all known services
  public register() {
    this.registerMixpanel();
  }
}

// Export a singleton instance
const inst = new Analytics();
export default inst;
